import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { IHospitalConfiguration, IUserDetails } from "../../@types/Login";
import { IPortalPreferences } from "../../@types/Preferences";
import ceMarkImage from "../../assets/images/ce-mark.svg";
import { SearchBar } from "../../components/SearchBar";
import { SupportButton } from "../../components/SupportButton";
import { TopPanelMenuComponent } from "../../components/TopPanelMenu";
import { UserSettingsDesktop } from "../../components/UserSettings";
import {
  AUTH_HOSPITAL_CONFIGURATION,
  AUTH_PORTAL_PREFERENCES,
  AUTH_USER_DETAILS,
} from "../../constants/localStorageKeys";
import { PMP_EMAIL_DOMAIN } from "../../constants/pMpConstants";
import { useAuth } from "../../hooks/useAuth";
import { useCurrentUserRole } from "../../hooks/useCurrentUserRole";
import {
  NavContentContainer,
  TopPanelItemsContainerDesktop,
} from "../../layout/components/DashboardLayout/DashboardLayout.styles";
import { PageLayout } from "../../layout/components/PageLayout";
import { mainRoutes } from "../../routes/main.routes";
import {
  ROLES,
  isSystemControllerUser,
} from "../../utils/isSystemControllerUser";
import { mixpanelActions } from "../../utils/mixpanel";
import { safeStorage, safeJsonParse } from "../../utils/storage";
import { LanguageSection } from "./components/LanguageSection";
import { MessagesPreferencesSection } from "./components/MessagesPreferencesSection";
import { PreferencesSection } from "./components/PreferencesSection";
import { SecuritySection } from "./components/SecuritySection";
import { StaffSection } from "./components/StaffSection";
import {
  CeMarkFooter,
  CeMarkLogo,
  CeMarkText,
  ContentContainer,
  SettingsPageConatiner,
  SettingsPageContent,
} from "./SettingsPage.styles";

export function SettingsPage() {
  const { t } = useTranslation();

  document.title = t("settings_documentTitle");

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { canViewRagTab } = useCurrentUserRole();

  useEffect(() => {
    mixpanelActions.track("Visited Screen: ProfileSettings");

    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated]);

  const portalPreferences = safeStorage.getItem(AUTH_PORTAL_PREFERENCES);

  const parsedPortalPreferences = safeJsonParse<IPortalPreferences>(
    portalPreferences,
    {} as IPortalPreferences
  );

  // EN-5014: the Staff Management section is admin-only. Gate on the Legacy Admin
  // role as well as the legacy is_admin flag so access stays correct if the two drift.
  const parsedUserDetailsForGate = safeJsonParse<IUserDetails>(
    safeStorage.getItem(AUTH_USER_DETAILS) ?? "",
    {} as IUserDetails
  );
  const isStaffAdmin =
    parsedPortalPreferences.isAdmin ||
    parsedUserDetailsForGate.role === ROLES.LEGACY_ADMIN;

  const renderTopBarMenuRoutes = () => {
    const tempRoutes = mainRoutes;

    const userDetails = safeStorage.getItem(AUTH_USER_DETAILS) ?? "";
    const parsedUserDetails = safeJsonParse<IUserDetails>(
      userDetails,
      {} as IUserDetails
    );

    if (parsedUserDetails.userEmail.includes(PMP_EMAIL_DOMAIN)) {
      const surveysIndex = tempRoutes.findIndex((x) => x.name === "Surveys");
      if (surveysIndex !== -1) {
        tempRoutes[surveysIndex].disabled = false;
      }

      const protocolsIndex = tempRoutes.findIndex((x) => x.name === "Protocol");
      if (protocolsIndex !== -1) {
        tempRoutes[protocolsIndex].disabled = false;
      }

      const careInfoIndex = tempRoutes.findIndex((x) => x.name === "Care Info");
      if (careInfoIndex !== -1) {
        tempRoutes[careInfoIndex].disabled = false;
      }

      const workflowsIndex = tempRoutes.findIndex(
        (x) => x.name === "Workflows"
      );
      if (careInfoIndex !== -1) {
        tempRoutes[workflowsIndex].disabled = false;
      }
    }

    const hospitalConfiguration =
      safeStorage.getItem(AUTH_HOSPITAL_CONFIGURATION) ?? "{}";

    const parsedHospitalConfiguration = safeJsonParse<IHospitalConfiguration>(
      hospitalConfiguration,
      {} as IHospitalConfiguration
    );

    if (parsedHospitalConfiguration.enableAppointments) {
      const appointmentsIndex = tempRoutes.findIndex(
        (x) => x.name === "Appointments"
      );

      if (appointmentsIndex !== -1) {
        tempRoutes[appointmentsIndex].disabled = false;
      }
    }

    if (canViewRagTab) {
      const ragIndex = tempRoutes.findIndex((x) => x.name === "RAG");
      if (ragIndex !== -1) {
        tempRoutes[ragIndex].disabled = false;
      }
    }

    return tempRoutes
      .filter(({ disabled }) => !disabled)
      .map(({ path, name, labelKey }) => (
        <Link
          key={`${Math.random()}-${path}`}
          to={path}
          style={{ whiteSpace: "nowrap" }}
        >
          {labelKey ? t(labelKey) : name}
        </Link>
      ));
  };

  // EN-4961: SystemController users only have access to the reports
  // dashboard — drop the main-portal nav (menu items + search) and point the
  // logo back to /reports so Settings doesn't expose links they can't use.
  // They also get a "Home" link back to /reports, their only page. Everyone
  // else keeps the main-portal nav: a "Home" to /reports beside it stranded
  // users whose /reports header has no way back (the user-menu portal toggle
  // is LegacyAdmin-only). Dual-portal users reach /reports from that toggle.
  const isSystemController = isSystemControllerUser();

  return (
    <>
      <TopPanelMenuComponent
        onLogoClick={() => navigate(isSystemController ? "/reports" : "/")}
      >
        {/*
          Render NavContentContainer even when empty — it has flexGrow: 1,
          so dropping it would collapse the bar and clip the avatar against
          the logo (EN-4961).
        */}
        <NavContentContainer>
          <TopPanelItemsContainerDesktop>
            {isSystemController ? (
              // Match ReportsLayout's tab markup byte-for-byte — same
              // Container CSS targets the <a> selector and applies the same
              // height / padding / margin, so the Home item sits at the same
              // x/y as when you're on /reports.
              <a
                href="/reports"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/reports");
                }}
                style={{ whiteSpace: "nowrap" }}
              >
                {t("reports_tabs_home")}
              </a>
            ) : (
              renderTopBarMenuRoutes()
            )}
          </TopPanelItemsContainerDesktop>
          {!isSystemController && (
            <>
              <SearchBar />

              <SupportButton
                style={{ marginRight: "10px", marginLeft: "35px" }}
              />
            </>
          )}
        </NavContentContainer>

        <UserSettingsDesktop />
      </TopPanelMenuComponent>
      <ContentContainer>
        <SettingsPageConatiner>
          <SettingsPageContent>
            <PageLayout>
              {isStaffAdmin ? <StaffSection /> : null}

              <MessagesPreferencesSection />

              <SecuritySection />

              <PreferencesSection />

              <LanguageSection />

              <CeMarkFooter>
                <CeMarkLogo src={ceMarkImage} alt={t("settings_ceMarkAlt")} />
                <CeMarkText>{t("settings_ceMarkText")}</CeMarkText>
              </CeMarkFooter>
            </PageLayout>
          </SettingsPageContent>
        </SettingsPageConatiner>
      </ContentContainer>
    </>
  );
}
